/**
 * Connecting the desktop app to an account without a login form.
 *
 * Asking someone to type a password into a desktop app is both weaker security
 * and worse experience than sending them to a browser they already trust and
 * are probably already signed into. So the machine asks to be connected, the
 * person approves it there, and the app takes the session that grants.
 *
 * The agent runs the whole exchange and reports on stdout. An earlier version
 * had the app collect the result and hand the device its half over stdin, which
 * could never work: Tauri cannot close a child's stdin, so the agent waited for
 * an end of input that never came while the app saw a write succeed. Reading a
 * child's output has no such hazard.
 */
import { Command } from '@tauri-apps/api/shell'
import { appWindow } from '@tauri-apps/api/window'
import { API_BASE, WEB_BASE, setToken } from './api'

const SIDECAR = 'binaries/nevtan-agent'

interface ConnectResult {
  connected: true
  agentId: string
  deviceName: string
  accessToken: string | null
}

/**
 * Runs the connect flow to completion.
 *
 * @param onUrl called with the approval URL as soon as the agent reports it,
 *              so it can be shown as well as opened — a browser that fails to
 *              launch should not leave someone with nothing to click.
 * @returns the session token granted by whoever approved
 */
export function connectAccount(onUrl: (url: string) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const command = Command.sidecar(SIDECAR, [
      'connect',
      '--backend',
      API_BASE,
      '--web',
      WEB_BASE,
      // Names the application the person is sitting in front of, so the
      // approval page returns them here rather than to whichever client it
      // would otherwise assume. Without it the page cannot tell this apart
      // from an editor asking, and someone who signed in from VS Code was
      // told NevTan AI was opening.
      '--client',
      'desktop',
    ])

    let settled = false
    const fail = (message: string) => {
      if (!settled) {
        settled = true
        reject(new Error(message))
      }
    }

    command.stdout.on('data', (line: string) => {
      const trimmed = line.trim()
      if (!trimmed.startsWith('{')) return
      try {
        const message = JSON.parse(trimmed) as { approvalUrl?: string } & Partial<ConnectResult>
        if (message.approvalUrl) {
          onUrl(message.approvalUrl)
          return
        }
        if (message.connected && message.accessToken) {
          settled = true
          setToken(message.accessToken)
          void comeForward()
          resolve(message.accessToken)
        }
      } catch {
        // Not every line is JSON; anything unparseable is progress noise.
      }
    })

    // The agent writes its progress and its errors here, so this is where a
    // failure explains itself.
    let stderr = ''
    command.stderr.on('data', (line: string) => {
      stderr += `${line}\n`
    })

    command.on('close', ({ code }: { code: number | null }) => {
      if (settled) return
      const detail = stderr.trim().split('\n').filter(Boolean).pop()
      fail(detail || `Connecting this device failed (exit ${code ?? 'unknown'})`)
    })

    command.on('error', (message: string) => fail(message))

    command.spawn().catch((e: unknown) => {
      fail(e instanceof Error ? e.message : 'Could not start the NevTan agent')
    })
  })
}

/**
 * Brings this window back in front once the browser has finished with it.
 *
 * <p>The approval page also asks for this, by opening `nevtan://` -- but that
 * is a scheme somebody has to have registered, and it is the long way round
 * for a window belonging to the process that is already watching the pairing
 * finish. Raising it from here cannot open a second window, because there is
 * no launching involved: it is this one.
 *
 * <p>Unminimising first, because a minimised window cannot be focused, and
 * unconditionally because asking whether it is minimised costs a second call
 * to answer a question whose wrong answer is harmless. Both are best-effort:
 * an operating system may refuse to let a background application take the
 * foreground, in which case the window flashes in the taskbar and the person
 * arrives a moment later under their own steam.
 */
async function comeForward(): Promise<void> {
  try {
    await appWindow.unminimize()
    await appWindow.setFocus()
  } catch {
    // Not fatal, and not worth reporting: the sign-in succeeded either way,
    // and the window is where it always was.
  }
}
